import toast from "solid-toast";

type StartListening = (sourceLang: string, targetLang: string | null) => Promise<void>;

/**
 * Start flow used by the host:
 * - reset in-memory state (suggestions/insights)
 * - validate required fields
 * - optionally resolve geolocation and persist to case metadata
 * - start transcription
 */
export function useStartWithGeo(input: {
  sessionId: () => string | null;
  contactType: () => string;
  respondentName: () => string;
  sourceLang: () => string;
  targetLang: () => string;
  setShowValidation: (v: boolean) => void;
  setAutoLocation: (v: string) => void;
  startListening: StartListening;
  resetSuggestionsHistory: () => void;
  clearSuggestions: () => void;
  resetInsights: () => void;
  getSession: (id: string) => Promise<{ caseId?: string | null }>;
}) {
  async function persistAutoLocationToCase(autoLocation: string) {
    const sessionId = input.sessionId();
    if (!sessionId) return;

    try {
      const session = await input.getSession(sessionId);
      if (!session.caseId) return;

      const { saveCaseData } = await import("../../utils/caseData");
      await saveCaseData(session.caseId, { autoLocation });
    } catch (e) {
      console.error("Failed to save location to case:", e);
    }
  }

  async function resolveLocationFromBrowser(): Promise<string> {
    if (!("geolocation" in navigator)) return "";

    return await new Promise<string>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            const key = import.meta.env.VITE_MAPS_KEY as string | undefined;
            if (!key) {
              resolve(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
              return;
            }

            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${key}`,
            );
            if (!response.ok) throw new Error(await response.text());
            const data = await response.json();
            const formatted = data?.results?.[0]?.formatted_address;
            resolve(formatted || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          } catch (e) {
            console.warn("Reverse-geocode failed, using coords", e);
            resolve(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          }
        },
        (err) => {
          console.warn("Geolocation denied or failed", err);
          resolve("");
        },
      );
    });
  }

  async function startWithGeo() {
    // Clear any stale state from prior sessions before starting
    try {
      input.resetSuggestionsHistory();
      input.clearSuggestions();
      input.resetInsights();
    } catch (e) {
      console.warn("Failed to reset in-memory session state:", e);
    }

    const missingFields: string[] = [];
    if (!input.contactType()) missingFields.push("Contact Type");
    if (!input.respondentName()) missingFields.push("Respondent Name");

    if (missingFields.length > 0) {
      input.setShowValidation(true);
      toast.error(`Please provide ${missingFields.join(" and ")} before starting`);
      return;
    }

    const location = await resolveLocationFromBrowser();
    input.setAutoLocation(location);
    if (location) {
      persistAutoLocationToCase(location);
    }

    await input.startListening(input.sourceLang(), input.targetLang() || null);
  }

  return { startWithGeo };
}

